import dayjs from "dayjs";
import { UserCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { contactDisplay, leadDisplayName } from "@/lib/contact";
import LeadTagChips from "@/components/leadTags/LeadTagChips";
import { progressLabel } from "../progress";
import type { InactivoLead } from "../types";

type Props = {
  lead: InactivoLead;
  selected: boolean;
  selectable: boolean;
  // Show the specific progress tag (merged cold lane bundles three progresses).
  showProgressTag?: boolean;
  onToggle: () => void;
  onOpen: () => void;
};

const InactivoCard = ({ lead, selected, selectable, showProgressTag, onToggle, onOpen }: Props) => {
  // Inactivos rows come from Tinybird, which carries no username — a phoneless
  // lead resolves to "Sin nombre" rather than exposing the raw BSUID.
  const displayName = leadDisplayName(lead);
  const contact = contactDisplay(lead);
  // Unassessed leads have no assessedAt — fall back to the last update
  // (when they were moved to inactive).
  const inactiveSince = lead.assessmentAssessedAt ?? lead.updatedAt;
  const dias = Math.max(0, dayjs().diff(dayjs(inactiveSince), "day"));
  // A lead re-pinned outside the inactive position was handed off to a human.
  const wasReassigned = lead.pipelinePositionId !== "inactive";

  return (
    <div
      className={cn(
        "cursor-pointer space-y-2 rounded-md border bg-surface p-3 transition-colors",
        selected
          ? "border-palomma-primary ring-1 ring-palomma-primary/30"
          : "border-neutral-300 hover:border-neutral-600/40"
      )}
      onClick={onOpen}
    >
      <div className="flex items-start gap-2">
        {selectable ? (
          // stopPropagation only — the Checkbox's own onCheckedChange does the
          // toggle; calling onToggle here too would immediately undo it.
          <div className="flex min-h-6 items-center" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={selected} onCheckedChange={onToggle} />
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-neutral-900">{displayName}</div>
          {contact.kind !== "none" && (
            <div className="truncate text-xs text-neutral-600">{contact.value}</div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {showProgressTag ? (
          <span className="rounded-pill bg-warning-light px-2 py-0.5 text-[10px] font-medium text-neutral-900">
            {progressLabel(lead.assessmentProgress)}
          </span>
        ) : null}
        {wasReassigned ? (
          <span className="flex items-center gap-1 rounded-pill bg-success-light px-2 py-0.5 text-[10px] font-medium text-neutral-900">
            <UserCheck className="size-3" />
            Asignado
          </span>
        ) : null}
      </div>
      <LeadTagChips tagIds={lead.tagIds} />
      <div className="text-xs text-neutral-600">
        {dias === 0 ? "Hoy" : `Hace ${dias} día${dias === 1 ? "" : "s"}`}
      </div>
    </div>
  );
};

export default InactivoCard;
